import httpClient from 'api/httpClient';
import {
  Enterprise,
  EnterpriseBusinessUnit,
  EnterpriseLocation,
  EnterpriseMember,
  EnterpriseSeniority,
  SheetEnterpriseMember,
  UpdateEnterpriseMember,
  UpdateEnterpriseValue,
} from 'models/Enterprises';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { AxiosRequestConfig } from 'axios';
import { UploadedFileResponse } from 'models/File';
import {
  createFileFormData,
  getFileParams,
  getUploadedFile,
} from 'uploadFileUtils';
import { IdentityUserInfo } from 'models/User';
import { Params } from 'react-router-dom';
import { DeleteBusinessUnit } from 'components/DeleteBusinessUnitModal/DeleteBusinessUnitModal.component';
import { DeleteSeniority } from 'components/DeleteMemberSeniorityModal/DeleteMemberSeniorityModal.component';
import { DeleteLocation } from 'components/DeleteMemberLocationModal/DeleteMemberLocationModal.component';

function createInitial() {
  return httpClient.post<Enterprise>('enterprises');
}

function getEnterprise(id: string) {
  return httpClient.get<Enterprise>(`enterprises/${id}`);
}

function getEnterprises(params?: PaginationParams & Params) {
  return httpClient.get<PaginatedData<Enterprise>>('enterprises', { params });
}

function updateEnterprise(id: string, data: UpdateEnterpriseValue) {
  return httpClient.patch<Enterprise>(`enterprises/${id}`, data);
}

function publishEnterprise(id: string, data: UpdateEnterpriseValue) {
  return httpClient.post<Enterprise>(`enterprises/${id}/publish`, data);
}

async function uploadLogo(
  file: File,
  fileName: string,
  enterpriseId: string,
  config?: AxiosRequestConfig,
) {
  const fileParams = getFileParams(fileName, file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `enterprises/${enterpriseId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

function deleteLogo(fileName: string, fileId: string, enterpriseId: string) {
  return httpClient.delete<{ fileId: string }>(
    `enterprises/${enterpriseId}/files/${fileName}/${fileId}`,
  );
}

function createInitialMember(id: string) {
  return httpClient.post<EnterpriseMember>(
    `enterprise-members/enterprises/${id}`,
  );
}

function getEnterprisePeople(id: string, params?: Params) {
  return httpClient.get<PaginatedData<EnterpriseMember>>(
    `enterprise-members/enterprises/${id}`,
    {
      params,
    },
  );
}

function updateEnterprisePerson(id: string, data: UpdateEnterpriseMember) {
  return httpClient.patch<EnterpriseMember>(`enterprise-members/${id}`, data);
}

function addEnterprisePerson(id: string, data: UpdateEnterpriseMember) {
  return httpClient.post<EnterpriseMember>(
    `enterprise-members/${id}/publish`,
    data,
  );
}

async function uploadProfileImage(
  file: File,
  fileName: string,
  memberId: string,
  config?: AxiosRequestConfig,
) {
  const fileParams = getFileParams(fileName, file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `enterprise-members/${memberId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

function deleteProfileImage(
  fileName: string,
  fileId: string,
  memberId: string,
) {
  return httpClient.delete<{ fileId: string }>(
    `enterprise-members/${memberId}/files/${fileName}/${fileId}`,
  );
}

function sendInvites(id: string, enterpriseMemberIds: string[]) {
  return httpClient.post(`enterprise-members/enterprises/${id}/send-invite`, {
    enterpriseMemberIds,
  });
}

function deleteMember(enterpriseMemberId: string) {
  return httpClient.post(
    `enterprise-members/${enterpriseMemberId}/delete-request`,
  );
}

function undoDeleteMember(enterpriseMemberId: string) {
  return httpClient.post(
    `enterprise-members/${enterpriseMemberId}/undo-delete-request`,
  );
}

function bulkDeleteMembers(id: string, enterpriseMemberIds: string[]) {
  return httpClient.post(
    `enterprise-members/enterprises/${id}/bulk-delete-request`,
    {
      enterpriseMemberIds,
    },
  );
}

function undoBulkDeleteMembers(id: string) {
  return httpClient.post(
    `enterprise-members/enterprises/${id}/undo-bulk-delete-request`,
  );
}

function getBulkSpreadsheetTemplate(id: string) {
  return httpClient.get<string>(
    `enterprise-members/enterprises/${id}/template`,
  );
}

function uploadBulkSpreadsheetTemplate(
  id: string,
  file: File,
  fileName: string,
  config: AxiosRequestConfig,
) {
  const formData = createFileFormData(fileName, file);

  return httpClient.post<SheetEnterpriseMember[]>(
    `enterprise-members/enterprises/${id}/bulk-upload`,
    formData,
    config,
  );
}

function getEnterpriseMembers(enterpriseId: string) {
  return httpClient.get<IdentityUserInfo[]>(
    `enterprise-members/enterprises/${enterpriseId}/colleagues`,
  );
}

function getEnterpriseFilters() {
  return httpClient.get<
    { name: string; count: number; enterpriseEntityId?: string }[]
  >('portfolio/deal-rooms-enterprise-filters');
}

function getBossModeRWC() {
  return httpClient.get<Enterprise>('enterprises/rwc');
}

function createBusinessUnit(enterpriseId: string, name: string) {
  return httpClient.post<EnterpriseBusinessUnit>(
    `enterprise-business-units/enterprises/${enterpriseId}`,
    { name },
  );
}

function updateBusinessUnit(enterpriseBusinessUnitId: string, name: string) {
  return httpClient.patch(
    `enterprise-business-units/${enterpriseBusinessUnitId}`,
    { name },
  );
}

function deleteBusinessUnit(
  enterpriseBusinessUnitId: string,
  data?: DeleteBusinessUnit,
) {
  return httpClient.post<EnterpriseBusinessUnit[]>(
    `enterprise-business-units/${enterpriseBusinessUnitId}/delete`,
    data,
  );
}

function getEnterpriseMembersForBusinessUnit(enterpriseBusinessUnitId: string) {
  return httpClient.get<EnterpriseMember[]>(
    `enterprise-members/enterprise-business-units/${enterpriseBusinessUnitId}`,
  );
}

function createMemberSeniority(enterpriseId: string, name: string) {
  return httpClient.post<EnterpriseSeniority>(
    `enterprise-seniorities/enterprises/${enterpriseId}`,
    { name },
  );
}

function updateMemberSeniority(enterpriseSeniorityId: string, name: string) {
  return httpClient.patch(`enterprise-seniorities/${enterpriseSeniorityId}`, {
    name,
  });
}

function deleteMemberSeniority(
  enterpriseSeniorityId: string,
  data?: DeleteSeniority,
) {
  return httpClient.post<EnterpriseSeniority[]>(
    `enterprise-seniorities/${enterpriseSeniorityId}/delete`,
    data,
  );
}

function getEnterpriseMembersForSeniority(enterpriseSeniorityId: string) {
  return httpClient.get<EnterpriseMember[]>(
    `enterprise-members/enterprise-seniorities/${enterpriseSeniorityId}`,
  );
}

function createMemberLocation(enterpriseId: string, name: string) {
  return httpClient.post<EnterpriseLocation>(
    `enterprise-locations/enterprises/${enterpriseId}`,
    { name },
  );
}

function updateMemberLocation(enterpriseLocationId: string, name: string) {
  return httpClient.patch(`enterprise-locations/${enterpriseLocationId}`, {
    name,
  });
}

function deleteMemberLocation(
  enterpriseLocationId: string,
  data?: DeleteLocation,
) {
  return httpClient.post<EnterpriseLocation[]>(
    `enterprise-locations/${enterpriseLocationId}/delete`,
    data,
  );
}

function getEnterpriseMembersForLocation(enterpriseLocationId: string) {
  return httpClient.get<EnterpriseMember[]>(
    `enterprise-members/enterprise-locations/${enterpriseLocationId}`,
  );
}

function bulkCreateMembers(
  enterpriseId: string,
  enterpriseMembers: Partial<SheetEnterpriseMember>[],
) {
  return httpClient.post(
    `enterprise-members/enterprises/${enterpriseId}/bulk-create`,
    {
      enterpriseMembers,
    },
  );
}

function getEnterpriseNameOptions(searchString: string) {
  return httpClient.get<string[]>('enterprises/enterprise-names', {
    params: { $searchString: searchString },
  });
}

function getEnterprisePrincipalOptions(searchString: string) {
  return httpClient.get<string[]>('enterprises/enterprise-principals', {
    params: { $searchString: searchString },
  });
}

export default {
  getEnterprise,
  getEnterprises,
  createInitial,
  updateEnterprise,
  publishEnterprise,
  uploadLogo,
  deleteLogo,
  getEnterprisePeople,
  updateEnterprisePerson,
  addEnterprisePerson,
  uploadProfileImage,
  deleteProfileImage,
  createInitialMember,
  sendInvites,
  deleteMember,
  undoDeleteMember,
  bulkDeleteMembers,
  undoBulkDeleteMembers,
  getBulkSpreadsheetTemplate,
  uploadBulkSpreadsheetTemplate,
  getEnterpriseMembers,
  getEnterpriseFilters,
  getBossModeRWC,
  createBusinessUnit,
  updateBusinessUnit,
  deleteBusinessUnit,
  getEnterpriseMembersForBusinessUnit,
  createMemberSeniority,
  updateMemberSeniority,
  deleteMemberSeniority,
  getEnterpriseMembersForSeniority,
  createMemberLocation,
  updateMemberLocation,
  deleteMemberLocation,
  getEnterpriseMembersForLocation,
  bulkCreateMembers,
  getEnterpriseNameOptions,
  getEnterprisePrincipalOptions,
};
